/**
 * O que precisa atravessar o redirecionamento para o Keycloak.
 *
 * Entre "ir para o Keycloak" e "voltar com o codigo" a pagina e descarregada:
 * memoria nao serve. O que fica guardado aqui e o minimo, e nada disso e
 * credencial:
 *
 * - `verificador`: o segredo de uso unico do PKCE, que so vale para trocar
 *   ESTE codigo e expira em minutos (RFC 7636). Ver pkce.ts.
 * - `estado`: valor opaco conferido na volta, contra resposta plantada.
 * - `destino`: o caminho interno para onde levar o usuario depois de entrar.
 *
 * Fica em `sessionStorage`, nao em `localStorage`: morre com a aba, nao viaja
 * para outra janela e nao sobrevive ao navegador fechado. Token de acesso, de
 * renovacao e de identidade nao passam por aqui em nenhuma hipotese —
 * armazem-token.ts explica por que.
 */

const CHAVE_PEDIDO = 'teg.pedido-autorizacao';
const CHAVE_SESSAO_CONHECIDA = 'teg.sessao-conhecida';
const CHAVE_TENTATIVA_SILENCIOSA = 'teg.tentativa-silenciosa';
const CHAVE_RESULTADO_DA_ACAO = 'teg.resultado-acao-keycloak';

/** Um pedido de autorizacao vale 10 minutos. Depois disso e resto de aba velha. */
const VALIDADE_PEDIDO_MS = 10 * 60 * 1000;

export interface PedidoAutorizacao {
  verificador: string;
  estado: string;
  /** Caminho interno de destino, sempre relativo. Nunca URL absoluta. */
  destino: string;
  criadoEm: number;
}

/**
 * Acesso defensivo ao armazenamento da sessao.
 *
 * Navegador com armazenamento bloqueado (modo restrito, politica corporativa)
 * lanca ao so tocar em `sessionStorage`. Nesse caso a entrada nao funciona, mas
 * a aplicacao nao pode explodir com erro tecnico na tela.
 */
function armazenamento(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function guardarPedido(
  pedido: Omit<PedidoAutorizacao, 'criadoEm'>,
  agora = Date.now(),
): void {
  const deposito = armazenamento();
  if (!deposito) {
    return;
  }
  try {
    deposito.setItem(CHAVE_PEDIDO, JSON.stringify({ ...pedido, criadoEm: agora }));
  } catch {
    // Sem espaco ou sem permissao: o fluxo falha adiante com mensagem amigavel.
  }
}

/**
 * Le e APAGA o pedido. Consumo unico: um codigo de autorizacao so pode ser
 * trocado uma vez, e deixar o verificador para tras convidaria a reuso.
 */
export function consumirPedido(agora = Date.now()): PedidoAutorizacao | null {
  const deposito = armazenamento();
  if (!deposito) {
    return null;
  }

  const bruto = deposito.getItem(CHAVE_PEDIDO);
  deposito.removeItem(CHAVE_PEDIDO);

  if (!bruto) {
    return null;
  }

  try {
    const dados = JSON.parse(bruto) as Partial<PedidoAutorizacao>;
    if (
      typeof dados.verificador !== 'string' ||
      typeof dados.estado !== 'string' ||
      typeof dados.destino !== 'string' ||
      typeof dados.criadoEm !== 'number'
    ) {
      return null;
    }
    if (agora - dados.criadoEm > VALIDADE_PEDIDO_MS) {
      return null;
    }
    return dados as PedidoAutorizacao;
  } catch {
    return null;
  }
}

export function descartarPedido(): void {
  armazenamento()?.removeItem(CHAVE_PEDIDO);
}

/**
 * Marca que ESTA aba ja teve uma sessao. E so um sim ou nao, sem identidade
 * nenhuma: serve para decidir se vale a pena tentar a autenticacao silenciosa
 * ao recarregar, em vez de mandar todo visitante anonimo passear pelo Keycloak.
 */
export function marcarSessaoConhecida(): void {
  try {
    armazenamento()?.setItem(CHAVE_SESSAO_CONHECIDA, '1');
  } catch {
    // Sem armazenamento, a autenticacao silenciosa simplesmente nao e tentada.
  }
}

export function esquecerSessaoConhecida(): void {
  const deposito = armazenamento();
  deposito?.removeItem(CHAVE_SESSAO_CONHECIDA);
  deposito?.removeItem(CHAVE_TENTATIVA_SILENCIOSA);
}

export function houveSessaoConhecida(): boolean {
  return armazenamento()?.getItem(CHAVE_SESSAO_CONHECIDA) === '1';
}

/**
 * Trava contra laco de redirecionamento.
 *
 * A autenticacao silenciosa e um redirecionamento para o Keycloak e de volta.
 * Se ela falhar em silencio e a aplicacao tentar de novo, o navegador entra em
 * um vaivem sem fim. A marca so e limpa por entrada bem-sucedida ou por saida.
 */
export function marcarTentativaSilenciosa(agora = Date.now()): void {
  try {
    armazenamento()?.setItem(CHAVE_TENTATIVA_SILENCIOSA, String(agora));
  } catch {
    // Ver guardarPedido.
  }
}

export function jaTentouSilenciosa(agora = Date.now()): boolean {
  const bruto = armazenamento()?.getItem(CHAVE_TENTATIVA_SILENCIOSA);
  if (!bruto) {
    return false;
  }
  const quando = Number.parseInt(bruto, 10);
  if (!Number.isFinite(quando)) {
    return true;
  }
  // Depois da validade, uma nova tentativa e legitima: o usuario pode ter
  // entrado em outra aba nesse meio-tempo.
  return agora - quando < VALIDADE_PEDIDO_MS;
}

export function limparTentativaSilenciosa(): void {
  armazenamento()?.removeItem(CHAVE_TENTATIVA_SILENCIOSA);
}

/**
 * O resultado da troca pedida ao Keycloak (`kc_action_status` na volta):
 * `success`, `cancelled` ou `error`. O `error` e o descarte: com outra acao
 * pendente na conta (trocar a senha, por exemplo), o Keycloak cumpre a
 * pendente e NAO faz a troca pedida (SegundoFator.
 * descartarAcaoDaAplicacaoComOutrasPendentes, no modulo Java). Guardado aqui
 * porque a volta passa pela tela de login antes de cair no "Meu perfil", que
 * le — e apaga — o resultado.
 */
export type ResultadoDaAcao = 'success' | 'cancelled' | 'error';

export function guardarResultadoDaAcao(resultado: string | null): void {
  const deposito = armazenamento();
  if (!deposito) {
    return;
  }
  try {
    if (resultado === 'success' || resultado === 'cancelled' || resultado === 'error') {
      deposito.setItem(CHAVE_RESULTADO_DA_ACAO, resultado);
    } else {
      deposito.removeItem(CHAVE_RESULTADO_DA_ACAO);
    }
  } catch {
    // Sem armazenamento, a tela so deixa de avisar; a conta nao muda por isso.
  }
}

/** Le e APAGA o resultado: o aviso aparece uma vez. */
export function consumirResultadoDaAcao(): ResultadoDaAcao | null {
  const deposito = armazenamento();
  if (!deposito) {
    return null;
  }
  try {
    const valor = deposito.getItem(CHAVE_RESULTADO_DA_ACAO);
    deposito.removeItem(CHAVE_RESULTADO_DA_ACAO);
    return valor === 'success' || valor === 'cancelled' || valor === 'error' ? valor : null;
  } catch {
    return null;
  }
}
